/**
 * LLM Model Registry Service
 * API calls for managing the LLM model catalog
 */

import type { LLMModel, CreateLLMModelRequest, UpdateLLMModelRequest, LLMModelOption } from '../types/llmModel';
import { authFetch, handleSessionExpired } from './authService';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // authFetch a déjà tenté le refresh — session réellement expirée.
    handleSessionExpired(response);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || 'Erreur réseau');
  }

  return response.json();
}

export const llmModelService = {
  getAll: async (activeOnly: boolean = false): Promise<LLMModel[]> => {
    const params = new URLSearchParams();
    if (activeOnly) {
      params.append('active_only', 'true');
    }
    const response = await authFetch(`${API_BASE}/admin/llm-models?${params}`);
    return handleResponse<LLMModel[]>(response);
  },

  getById: async (id: string): Promise<LLMModel> => {
    const response = await authFetch(`${API_BASE}/admin/llm-models/${id}`);
    return handleResponse<LLMModel>(response);
  },

  create: async (data: CreateLLMModelRequest): Promise<LLMModel> => {
    const response = await authFetch(`${API_BASE}/admin/llm-models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<LLMModel>(response);
  },

  update: async (id: string, data: UpdateLLMModelRequest): Promise<LLMModel> => {
    const response = await authFetch(`${API_BASE}/admin/llm-models/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<LLMModel>(response);
  },

  delete: async (id: string): Promise<void> => {
    const response = await authFetch(`${API_BASE}/admin/llm-models/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  getModelOptions: async (): Promise<LLMModelOption[]> => {
    const models = await llmModelService.getAll(true);
    return models.map(model => ({
      model_name: model.model_name,
      display_name: model.display_name,
      provider: model.provider,
    }));
  },
};
